import {FC, useEffect, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {Button} from '@/components/ui/button';
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from '@/components/ui/form';
import OwnInnIcon from '@/components/icons/OwnInnIcon';
import {useTranslation} from 'react-i18next';
import {Input} from '@/components/ui/input';
import ShowIcon from '@/components/icons/ShowIcon';
import HideIcon from '@/components/icons/HideIcon';
import EToast from '@/components/everridge/EToast.tsx';
import {useLocation, useNavigate} from 'react-router-dom';
import {cn} from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import {AppConfig} from '@/config/app.config';
import {AxiosError} from 'axios';
import {ProblemDetails} from '@/@types/base.types';
import {LoginResponseDto, TwoFactorAuthResponseDto} from '@/@types/auth.types.ts';
import {useCompleteMigrationApi} from '@/features/identity/hooks/useCompleteMigrationApi.ts';
import {useSignIn} from '@/features/identity/hooks/useSignIn.ts';
import {useAuthForm} from '@/features/identity/hooks/useAuthForm.ts';
import {AuthHelper} from '@/utils/auth.helper.ts';

const errorKeys = {
    tokenNotFound: 'token_not_found',
    tokenExpired: 'token_expired',
    tokenInvalid: 'invalid_token_type',
};

type MigrationLocationState = {
    migrationToken?: string;
    email?: string;
};

const NewPasswordForm: FC = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const signIn = useSignIn();
    const {setTwoFactorToken, setEmail} = useAuthForm();

    const state = (location.state as MigrationLocationState) ?? {};
    const migrationToken = state.migrationToken;

    const FormSchema = z
        .object({
            newPassword: z.string().min(1),
            confirmPassword: z.string().min(1),
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
            message: t('auth.newPassword.form.confirm.error'),
            path: ['confirmPassword'],
        });

    const [showPassword, setShowPassword] = useState<{
        [key in 'newPassword' | 'confirmPassword']: 'text' | 'password';
    }>({
        newPassword: 'password',
        confirmPassword: 'password',
    });

    const {doCompleteMigration} = useCompleteMigrationApi({onSuccess, onError});

    useEffect(() => {
        if (!migrationToken) {
            EToast({
                variant: 'error',
                message: t('auth.newPassword.toasts.missing.message'),
                description: t('auth.newPassword.toasts.missing.description'),
            });
            navigate('/auth/login', {replace: true});
        }
    }, [migrationToken]);

    function onSuccess(result: LoginResponseDto | TwoFactorAuthResponseDto) {
        if ('twoFactorRequired' in result && result.token) {
            setTwoFactorToken(result.token);
            if (state.email) setEmail(state.email);
            navigate('/auth/two-factor-login/');
            return;
        }

        const loginResponse = result as LoginResponseDto;
        const claims = AuthHelper.parseJwtTokenClaims(loginResponse.tokens.accessToken);
        const user = AuthHelper.createUser(loginResponse.user, claims);

        if (!user) {
            EToast({
                variant: 'error',
                message: t('auth.loginForm.error.message'),
                description: t('auth.loginForm.error.description'),
            });
            return;
        }

        signIn({tokens: loginResponse.tokens, user});
        navigate('/auth/choose-hotel', {replace: true});
    }

    function onError(error: AxiosError<ProblemDetails>) {
        const errorKey = error.response?.data?.code;

        const errorDescriptions: Record<string, string> = {
            [errorKeys.tokenExpired]: t('auth.newPassword.toasts.error.descriptionTokenExpired'),
            [errorKeys.tokenInvalid]: t('auth.newPassword.toasts.error.descriptionTokenInvalid'),
            [errorKeys.tokenNotFound]: t('auth.newPassword.toasts.error.descriptionTokenInvalid'),
        };

        EToast({
            variant: 'error',
            message: t('auth.newPassword.toasts.error.message'),
            description:
                errorKey && errorDescriptions[errorKey]
                    ? errorDescriptions[errorKey]
                    : t('auth.newPassword.toasts.error.descriptionDefault'),
        });
    }

    const form = useForm<z.infer<typeof FormSchema>>({
        resolver: zodResolver(FormSchema),
        defaultValues: {newPassword: '', confirmPassword: ''},
    });

    async function onSubmit(data: z.infer<typeof FormSchema>) {
        if (!migrationToken) return;
        await doCompleteMigration({token: migrationToken, newPassword: data.newPassword});
    }

    const showPasswordHandler = (field: keyof typeof showPassword) => {
        setShowPassword((prev) => ({
            ...prev,
            [field]: prev[field] === 'password' ? 'text' : 'password',
        }));
    };

    const inputClasses =
        'h-11 p-3.5 border border-popover-100 bg-popover-10 text-xs text-popover-800 placeholder:text-xs placeholder:text-popover-600 active:shadow-input-active error:shadow-input-error';

    return (
        <Form {...form}>
            <div
                className={cn(
                    'my-auto flex-1 flex flex-col gap-6 rounded-2.5xl bg-popover-10',
                    isMobile ? 'grow' : 'max-w-[20.5rem]',
                )}
            >
                <div className="flex flex-col items-center gap-4 text-center">
                    <OwnInnIcon className="size-11" />
                    <div className="w-[20.5rem] flex flex-col items-center gap-2">
                        <h3 className="text-2xl font-semibold select-none">
                            {t('auth.newPassword.text.title')}
                        </h3>
                        <p className="text-sm text-popover-800 font-medium">
                            {t('auth.newPassword.text.subtitle')}
                        </p>
                    </div>
                </div>
                <form
                    className="w-[20.5rem] flex flex-col gap-8"
                    onSubmit={form.handleSubmit(onSubmit)}
                >
                    <div className="flex flex-col gap-4">
                        <FormField
                            control={form.control}
                            name="newPassword"
                            render={({field}) => (
                                <FormItem className="flex flex-col gap-1.5">
                                    <FormLabel className="text-sm font-medium select-none">
                                        {t('elements.form.newPassword.label')}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                className={inputClasses}
                                                type={showPassword.newPassword}
                                                placeholder={t('elements.form.newPassword.placeholder')}
                                                {...field}
                                            />
                                            <Button
                                                type="button"
                                                variant="link"
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 h-fit p-0"
                                                onClick={() => showPasswordHandler('newPassword')}
                                            >
                                                {showPassword.newPassword === 'text' ? (
                                                    <ShowIcon />
                                                ) : (
                                                    <HideIcon />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="confirmPassword"
                            render={({field}) => (
                                <FormItem className="flex flex-col gap-1.5">
                                    <FormLabel className="text-sm font-medium select-none">
                                        {t('elements.form.confirmPassword.label')}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                className={inputClasses}
                                                type={showPassword.confirmPassword}
                                                placeholder={t('elements.form.confirmPassword.placeholder')}
                                                {...field}
                                            />
                                            <Button
                                                type="button"
                                                variant="link"
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 h-fit p-0"
                                                onClick={() => showPasswordHandler('confirmPassword')}
                                            >
                                                {showPassword.confirmPassword === 'text' ? (
                                                    <ShowIcon />
                                                ) : (
                                                    <HideIcon />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-col gap-4">
                        <Button type="submit">{t('elements.buttons.setNewPassword')}</Button>
                        <Button
                            onClick={() => navigate('/auth/login', {replace: true})}
                            type="button"
                            variant="link"
                            className="p-0 hover:text-popover-800 active:text-popover-600"
                        >
                            {t('elements.buttons.backToLogin')}
                        </Button>
                    </div>
                </form>
            </div>
        </Form>
    );
};

export default NewPasswordForm;
